<template>
  <div class="skill-management-panel">
    <div class="panel-header">
      <h3>Skill 能力管理</h3>
      <el-button type="primary" @click="openSkillDialog()">新增 Skill</el-button>
    </div>

    <el-table :data="skills" stripe style="width: 100%">
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column label="Skill 类型" width="120">
        <template #default="scope">
          <el-tag type="primary" size="small">{{ scope.row.skill_type }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="alias" label="别名" width="100" />
      <el-table-column prop="name" label="名称" width="130" />
      <el-table-column prop="description" label="描述" show-overflow-tooltip />
      <el-table-column label="解析器" width="100">
        <template #default="scope">
          <el-tag type="info" size="small">{{ scope.row.output_parser_type }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="90">
        <template #default="scope">
          <el-tag v-if="scope.row.is_system" type="warning" size="small">系统</el-tag>
          <el-tag v-else type="" size="small">自定义</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="scope">
          <el-switch
            :model-value="!!scope.row.is_active"
            :disabled="!!scope.row.is_system"
            @change="toggleSkill(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="display_order" label="排序" width="70" />
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="scope">
          <el-button link type="primary" size="small" @click="openSkillDialog(scope.row)">编辑</el-button>
          <el-button
            link
            type="danger"
            size="small"
            :disabled="!!scope.row.is_system"
            @click="deleteSkill(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Skill 编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingSkill.id ? '编辑 Skill' : '新增 Skill'"
      width="700px"
      :close-on-click-modal="false"
    >
      <el-form :model="editingSkill" label-width="120px" class="skill-form">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Skill 类型">
              <el-select
                v-model="editingSkill.skill_type"
                style="width: 100%"
                :disabled="!!editingSkill.id"
                placeholder="请选择类型"
              >
                <el-option label="curator（音乐推荐）" value="curator" />
                <el-option label="taste（品味分析）" value="taste" />
                <el-option label="chat（音乐闲聊）" value="chat" />
                <el-option label="intent（意图识别）" value="intent" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="名称">
              <el-input v-model="editingSkill.name" placeholder="例如: 音乐策划" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="别名">
              <el-input v-model="editingSkill.alias" placeholder="可选，例如: 我的推荐助手" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="输出解析器">
              <el-select v-model="editingSkill.output_parser_type" style="width: 100%">
                <el-option label="text（纯文本）" value="text" />
                <el-option label="dsv（分隔符格式）" value="dsv" />
                <el-option label="json（JSON 格式）" value="json" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="描述">
          <el-input v-model="editingSkill.description" type="textarea" :rows="2" placeholder="简短描述该 Skill 的功能" />
        </el-form-item>

        <el-form-item label="System Prompt">
          <el-input
            v-model="editingSkill.system_prompt"
            type="textarea"
            :rows="8"
            placeholder="AI 系统提示词，定义角色和行为规范"
          />
        </el-form-item>

        <el-form-item label="格式指令">
          <el-input
            v-model="editingSkill.format_instruction"
            type="textarea"
            :rows="5"
            placeholder="输出格式说明，例如 DSV 格式要求"
          />
        </el-form-item>

        <el-form-item label="Context Config">
          <el-input
            v-model="contextConfigText"
            type="textarea"
            :rows="5"
            placeholder='JSON 格式，例如 {"max_songs": 20}'
            :status="contextConfigError ? 'error' : ''"
          />
          <div v-if="contextConfigError" style="color: #f56c6c; font-size: 12px; margin-top: 4px;">
            {{ contextConfigError }}
          </div>
        </el-form-item>

        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="显示顺序">
              <el-input-number v-model="editingSkill.display_order" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="系统内置">
              <el-switch v-model="editingSkill.is_system" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="启用">
              <el-switch v-model="editingSkill.is_active" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveSkill">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';

// ---- 数据 ----
const skills = ref([]);
const dialogVisible = ref(false);
const saving = ref(false);
const contextConfigText = ref('{}');
const contextConfigError = ref('');

const emptySkill = () => ({
  id: null,
  skill_type: '',
  alias: '',
  name: '',
  description: '',
  system_prompt: '',
  format_instruction: '',
  context_config: {},
  output_parser_type: 'text',
  is_system: false,
  is_active: true,
  display_order: 0,
});

const editingSkill = reactive(emptySkill());

// ---- 监听 context_config 文本变化，实时校验 ----
watch(contextConfigText, (val) => {
  try {
    JSON.parse(val);
    contextConfigError.value = '';
  } catch {
    contextConfigError.value = 'JSON 格式不合法';
  }
});

// ---- 加载列表 ----
const loadSkills = async () => {
  try {
    const resp = await axios.get('/api/skills');
    if (resp.data.code === 1) {
      skills.value = resp.data.data;
    }
  } catch (err) {
    console.error('加载 Skill 列表失败', err);
  }
};

// ---- 打开弹窗 ----
const openSkillDialog = (row = null) => {
  if (row) {
    Object.assign(editingSkill, row);
    // context_config 已由后端解析为对象
    contextConfigText.value = JSON.stringify(row.context_config || {}, null, 2);
  } else {
    Object.assign(editingSkill, emptySkill());
    contextConfigText.value = '{}';
  }
  contextConfigError.value = '';
  dialogVisible.value = true;
};

// ---- 保存 ----
const saveSkill = async () => {
  if (!editingSkill.id && !editingSkill.skill_type) {
    ElMessage.warning('Skill 类型不能为空');
    return;
  }
  if (!editingSkill.name.trim()) {
    ElMessage.warning('名称不能为空');
    return;
  }

  // 校验 context_config JSON
  let contextConfig = {};
  try {
    contextConfig = JSON.parse(contextConfigText.value || '{}');
  } catch {
    ElMessage.warning('context_config JSON 格式不合法，请修正后再保存');
    return;
  }

  saving.value = true;
  try {
    const payload = {
      ...editingSkill,
      context_config: contextConfig,
    };

    let resp;
    if (editingSkill.id) {
      resp = await axios.put(`/api/skills/${editingSkill.id}`, payload);
    } else {
      resp = await axios.post('/api/skills', payload);
    }

    if (resp.data.code === 1) {
      ElMessage.success(editingSkill.id ? '更新成功' : '创建成功');
      dialogVisible.value = false;
      loadSkills();
    } else {
      ElMessage.error(resp.data.msg || '操作失败');
    }
  } catch (err) {
    console.error('保存 Skill 失败', err);
    ElMessage.error('保存失败');
  } finally {
    saving.value = false;
  }
};

// ---- 删除 ----
const deleteSkill = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除 Skill "${row.name}" 吗？`,
      '确认删除',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );

    const resp = await axios.delete(`/api/skills/${row.id}`);
    if (resp.data.code === 1) {
      ElMessage.success('删除成功');
      loadSkills();
    } else {
      ElMessage.error(resp.data.msg || '删除失败');
    }
  } catch (err) {
    if (err !== 'cancel' && err?.message !== 'cancel') {
      console.error('删除 Skill 失败', err);
      ElMessage.error('删除失败');
    }
  }
};

// ---- 切换激活状态 ----
const toggleSkill = async (row) => {
  try {
    const resp = await axios.put(`/api/skills/${row.id}/toggle`);
    if (resp.data.code === 1) {
      ElMessage.success(resp.data.msg || '操作成功');
      loadSkills();
    } else {
      ElMessage.error(resp.data.msg || '操作失败');
    }
  } catch (err) {
    console.error('切换 Skill 状态失败', err);
    ElMessage.error('操作失败');
  }
};

onMounted(() => {
  loadSkills();
});
</script>

<style scoped>
.skill-management-panel .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.skill-form .el-form-item {
  margin-bottom: 16px;
}
</style>
